import { Download, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { generateDocx } from "@/utils/docxGenerator";
import { cn } from "@/lib/utils";

const ProductBadge = ({ product }) => {
  const colorClasses = {
    "DataHub": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "AI Forecasts": "bg-violet-50 text-violet-700 border-violet-200",
    "Dispatch Solutions": "bg-orange-50 text-orange-700 border-orange-200",
    "Emission Intensity Forecasting": "bg-teal-50 text-teal-700 border-teal-200",
    "Signal Orchestrator": "bg-indigo-50 text-indigo-700 border-indigo-200",
  };

  return (
    <span className={cn(
      "inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full border",
      colorClasses[product] || "bg-slate-100 text-slate-700 border-slate-200"
    )}>
      {product}
    </span>
  );
};

const MarketBadge = ({ market }) => (
  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-sky-50 text-sky-700 border border-sky-200">
    {market}
  </span>
);

const IndustryBadge = ({ industry }) => (
  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-200">
    {industry}
  </span>
);

const highlightText = (text, query) => {
  if (!query || !text) return text;
  
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return parts.map((part, index) => 
    part.toLowerCase() === query.toLowerCase() 
      ? <mark key={index} className="bg-yellow-200 px-0.5 rounded">{part}</mark>
      : part
  );
};

const UseCaseCard = ({ useCase, searchQuery, onView, onDownload }) => {
  const handleDownload = async (e) => {
    e.stopPropagation();
    await generateDocx(useCase);
    onDownload();
  };

  return (
    <div
      className="use-case-card bg-white border border-slate-100 rounded-2xl overflow-hidden cursor-pointer hover:border-[#FF6F40]/30"
      onClick={onView}
      data-testid={`usecase-card-${useCase.id}`}
    >
      {/* Accent bar on hover */}
      <div className="h-1 bg-gradient-to-r from-[#FF6F40] to-[#FF8F6D] opacity-0 hover:opacity-100 transition-opacity" />
      
      <div className="p-5">
        {/* Title */}
        <h3 className="text-lg font-bold text-slate-900 font-['Manrope'] mb-2 line-clamp-2">
          {highlightText(useCase.title, searchQuery)}
        </h3>

        {/* Industry & Market badges */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {useCase.industry.slice(0, 2).map((ind) => (
            <IndustryBadge key={ind} industry={ind} />
          ))}
          {useCase.market.slice(0, 2).map((mkt) => (
            <MarketBadge key={mkt} market={mkt} />
          ))}
        </div>

        {/* Product badges */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {useCase.product.map((prod) => (
            <ProductBadge key={prod} product={prod} />
          ))}
        </div>

        {/* Summary */}
        <p className="text-sm text-slate-600 line-clamp-3 mb-4">
          {highlightText(useCase.summary, searchQuery)}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <span className="text-xs text-slate-400">
            For: <span className="text-slate-500 font-medium">{useCase.clientNote}</span>
          </span>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="h-8 px-2 text-slate-500 hover:text-[#FF6F40] hover:bg-[#FFF1EC]"
              data-testid={`download-btn-${useCase.id}`}
            >
              <Download className="w-4 h-4 mr-1" />
              <span className="text-xs font-medium">DOCX</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-slate-500 hover:text-[#FF6F40] hover:bg-[#FFF1EC]"
              data-testid={`view-btn-${useCase.id}`}
            >
              <Eye className="w-4 h-4 mr-1" />
              <span className="text-xs font-medium">View</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UseCaseCard;
